import { useParams } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { useClubBySlugQuery } from '@data/clubs/useClubBySlugQuery';
import { useArticlesQuery } from '@data/articles/useArticlesQuery';
import { useMatchesQuery } from '@data/matches/useMatchesQuery';
import { useLeagueTableQuery } from '@data/leagueTables/useLeagueTableQuery';
import { ClubOverview } from '@ui/club/components/ClubOverview/ClubOverview';
import { useIsMobile } from '../../utils/useIsMobile';
import { MatchPeriodFilter, MatchSort, MatchStatusFilter, TagsMatchMode } from '@repo/types';
import { getCachedImage } from '@ui/utils/getCachedImage';
import { ImageVariant } from '@ui/common/components/Image/contracts';
import { NotFoundPage } from '../NotFoundPage';
import { Loader } from '@ui/common/components/Loader/Loader';

export function ClubPage() {
  const params = useParams();
  const isMobile = useIsMobile();

  const { data: club, isLoading: isClubLoading, isError } = useClubBySlugQuery(params.slug);

  const firstTeam = club?.teams?.find((team) => team.isDefault);

  const { data: articles, isLoading: areArticlesLoading } = useArticlesQuery({
    tags: params.slug,
    tagsMatchMode: TagsMatchMode.All,
    limit: 4,
  });

  const { data: fixtures, isLoading: areFixturesLoading } = useMatchesQuery(
    {
      teamId: firstTeam?.id,
      limit: 3,
      status: MatchStatusFilter.FIXTURE,
      period: MatchPeriodFilter.Days,
      periodLimit: 90,
    },
    {
      enabled: Boolean(firstTeam?.id),
    }
  );

  const { data: results, isLoading: areResultsLoading } = useMatchesQuery(
    {
      teamId: firstTeam?.id,
      limit: 3,
      status: MatchStatusFilter.RESULT,
      sort: MatchSort.DESC,
      period: MatchPeriodFilter.Days,
      periodLimit: -30,
    },
    {
      enabled: Boolean(firstTeam?.id),
    }
  );

  const { data: leagueTable, isLoading: isLeagueTableLoading } = useLeagueTableQuery(
    {
      teamId: firstTeam?.id,
    },
    {
      enabled: !!firstTeam?.id,
    }
  );

  if (isClubLoading || areArticlesLoading || areFixturesLoading || areResultsLoading || isLeagueTableLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 100px)',
        }}
      >
        <Loader />
      </div>
    );
  }

  if (!params.slug || isError || !club) {
    return <NotFoundPage />;
  }

  const metaTitle = `${club.fullName} Team News, Fixtures and Results 2024/2025 | ${import.meta.env.VITE_SITE_NAME}`;
  const metaDescription = `For the latest news on ${
    club.fullName
  }, including scores, fixtures, team news, results, form guide and league position, visit the official website of the ${
    import.meta.env.VITE_SITE_NAME
  }.`;
  const image = getCachedImage(club.logo ?? '/site-logo.webp', ImageVariant.Detail);

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={image} />
        <meta property="og:image:alt" content={club.fullName} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={image} />
      </Helmet>
      <ClubOverview
        isMobile={isMobile}
        club={club}
        articles={articles}
        leagueTable={leagueTable}
        results={results}
        fixtures={fixtures}
      />
    </>
  );
}
